import { getResellBuybacks } from 'lib/api/reSell/factoring';
import { ResellBuyback } from 'lib/api/reSell/factoring/types';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { errorHandler } from 'lib/functions/errors';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import RevertBuybackDialog from '../../../../../ResellBuybacks/components/RevertBuybackDialog';
import { useFactoringResell } from '../../../../context';

const Buybacks: React.FC = () => {
  const { resell } = useFactoringResell();
  if (!resell) return null;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });
  const [buybacks, setBuybacks] = useState<ResellBuyback[]>([]);
  const [openRevertModal, setOpenRevertModal] = useState(false);
  const [selectedBuyback, setSelectedBuyback] = useState<ResellBuyback | null>(
    null,
  );

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    let query = '';
    if (page) query += `page=${page}&`;
    if (pageSize) query += `pageSize=${pageSize}&`;
    query += `resellId=${resell.id}&`;
    return query;
  };

  const fetchBuybacks = async ({
    page,
    pageSize,
    exportComplete,
  }: {
    page: number;
    pageSize: number;
    exportComplete?: boolean;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize, exportComplete });
      const response = await getResellBuybacks(dispatch, query);
      if (response) {
        setBuybacks(response.data);
        setPagination(response.pagination);
      }
      setLoading(false);
      return response;
    } catch (error) {
      errorHandler(dispatch, error, 'Error al obtener las recompras');
      setLoading(false);
      return undefined;
    }
  };

  const setUp = async () => {
    await fetchBuybacks({ page: 1, pageSize: 25 });
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleRevertBuyback = (id: number) => {
    const buyback = buybacks.find((buyback) => buyback.id === id);
    setSelectedBuyback(buyback || null);
    setOpenRevertModal(true);
  };

  const actions = [
    {
      action: handleRevertBuyback,
      actionLabel: 'Reversar',
      canShow: () =>
        validatePermissions(user, 'financing_fundSale_buybacks_delete'),
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title={`Recompras de venta #${resell.id}`}
        variant="body1"
        buttons={[]}
      />
      <TableWithPagination
        data={buybacks}
        setData={setBuybacks}
        dataHeaders={[
          { key: 'id', label: 'Id', type: 'numeric-id' },
          { key: 'folio', label: 'Folio' },
          { key: 'businessName', label: 'Cliente' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'fundName', label: 'Fondo' },
          { key: 'resellAmount', label: 'Monto venta', type: 'money' },
          { key: 'amount', label: 'Monto recompra', type: 'money' },
          { key: 'buybackAt', label: 'Fecha recompra', type: 'date' },
        ]}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchBuybacks}
        actions={actions}
        exportToFile="xlsx"
        exportComplete
        setLoading={setLoading}
      />
      {openRevertModal && selectedBuyback && (
        <RevertBuybackDialog
          open={openRevertModal}
          setOpen={setOpenRevertModal}
          buyback={selectedBuyback}
          setUp={setUp}
        />
      )}
    </div>
  );
};

export default Buybacks;
